import { useEffect, useRef } from 'react';
const SETUP_CACHE_KEY = 'hcd.setup.v1';
const SESSION_FLAG_KEY = 'hcd.session.v1';
const DISPLAY_SETTINGS_KEY = 'hcd.settings.v1';
const SKIP_AUTO_CONFIRM_KEY = 'hcd.skipAutoConfirm.v1';
export const loadSetupCache = () => {
    try {
        const raw = localStorage.getItem(SETUP_CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    }
    catch {
        return null;
    }
};
export const hasActiveSession = () => sessionStorage.getItem(SESSION_FLAG_KEY) === '1';
export const markSessionStarted = () => sessionStorage.setItem(SESSION_FLAG_KEY, '1');
export const skipAutoConfirmOnce = () => sessionStorage.setItem(SKIP_AUTO_CONFIRM_KEY, '1');
export const isAutoConfirmSkipped = () => sessionStorage.getItem(SKIP_AUTO_CONFIRM_KEY) === '1';
export const clearAutoConfirmSkip = () => sessionStorage.removeItem(SKIP_AUTO_CONFIRM_KEY);
export const saveSetupCache = (partial) => {
    const prev = loadSetupCache() ?? {};
    const next = { ...prev, ...partial, timestamp: Date.now() };
    localStorage.setItem(SETUP_CACHE_KEY, JSON.stringify(next));
};
export const clearSetupCache = () => {
    localStorage.removeItem(SETUP_CACHE_KEY);
};
const DEFAULT_DISPLAY_SETTINGS = {
    lineOrder: 'newestFirst',
    standbyDelay: 180,
};
const isLineOrder = (candidate) => candidate === 'newestFirst' || candidate === 'oldestFirst';
const parseStandbyDelay = (candidate) => {
    const seconds = Number(candidate);
    return seconds > 0 ? seconds : undefined;
};
const loadStoredDisplaySettings = () => {
    try {
        const raw = localStorage.getItem(DISPLAY_SETTINGS_KEY);
        return raw
            ? { ...DEFAULT_DISPLAY_SETTINGS, ...JSON.parse(raw) }
            : DEFAULT_DISPLAY_SETTINGS;
    }
    catch {
        return DEFAULT_DISPLAY_SETTINGS;
    }
};
export const loadDisplaySettings = () => {
    const storedSettings = loadStoredDisplaySettings();
    const urlLineOrder = getUrlParam('lineOrder');
    const urlStandbyDelay = parseStandbyDelay(getUrlParam('standbyDelay'));
    return {
        lineOrder: isLineOrder(urlLineOrder)
            ? urlLineOrder
            : storedSettings.lineOrder,
        standbyDelay: urlStandbyDelay ?? storedSettings.standbyDelay,
    };
};
export const isLineOrderInUrl = () => isLineOrder(getUrlParam('lineOrder'));
export const isStandbyDelayInUrl = () => parseStandbyDelay(getUrlParam('standbyDelay')) !== undefined;
export const saveDisplaySettings = (settings) => {
    localStorage.setItem(DISPLAY_SETTINGS_KEY, JSON.stringify(settings));
};
export const clearDisplaySettings = () => {
    localStorage.removeItem(DISPLAY_SETTINGS_KEY);
};
export const orderNewestFirst = (saleLines) => {
    const lineGroups = [];
    for (const line of saleLines) {
        const currentGroup = lineGroups.at(-1);
        if (line.isAddon && currentGroup)
            currentGroup.push(line);
        else
            lineGroups.push([line]);
    }
    return lineGroups.reverse().flat();
};
export const calculateSaleSummary = (saleLines) => {
    const formattedSaleLines = saleLines.map((line) => ({
        ...line,
        formattedAmountInclVat: line.amountInclVat.toFixed(2),
    }));
    const subtotal = saleLines.reduce((sum, line) => sum + line.amountInclVat, 0);
    const discount = saleLines.reduce((sum, line) => sum + line.discountAmount, 0);
    const tax = saleLines.reduce((sum, line) => sum + (line.amount * line.vatPercent) / 100, 0);
    const total = subtotal - discount;
    return {
        formattedSaleLines,
        subtotal,
        discount,
        tax,
        total,
    };
};
export const includeCorsProxy = (url) => {
    const encodedUrl = encodeURIComponent(url);
    return `https://corsproxy.npretail.app/corsproxy/?apiurl=${encodedUrl}`;
};
export const getHeaders = async (loginConfig) => {
    const headers = {
        'Content-Type': 'application/json',
    };
    if (loginConfig.isOnCloud && loginConfig.cloudConfig) {
        const customWindow = window;
        const token = customWindow?.loginConfig?.cloudConfig?.oAuthToken;
        if (!token) {
            console.error('oAuthToken is missing in loginConfig');
            return null;
        }
        headers['Authorization'] = `Bearer ${token}`;
    }
    else if (loginConfig.onPremiseConfig) {
        headers['Authorization'] = `Basic ${loginConfig.onPremiseConfig.basicAuthToken}`;
        headers['x-npr-api-remote-type'] = 'crane';
    }
    return headers;
};
export const getSaleApiConfig = (loginConfig) => {
    const { cloudConfig, onPremiseConfig, isOnCloud } = loginConfig;
    if (isOnCloud && cloudConfig) {
        return {
            environment: cloudConfig.environment,
            company: cloudConfig.company,
            accountSegment: cloudConfig.tenantId,
            posUnit: cloudConfig.posUnit,
        };
    }
    if (onPremiseConfig) {
        return {
            environment: onPremiseConfig.environment,
            company: onPremiseConfig.company,
            accountSegment: onPremiseConfig.tenantId,
            posUnit: onPremiseConfig.posUnit,
        };
    }
    return {
        environment: '',
        company: '',
        accountSegment: '',
        posUnit: '',
    };
};
export const getUrlParam = (param, search) => {
    const urlParams = new URLSearchParams(search ?? window.location.search);
    return urlParams.get(param);
};
export const findCaseInsensitiveMatch = (items, value, accessor) => {
    if (!value)
        return undefined;
    const lowerValue = value.toLowerCase();
    return items.find((item) => accessor(item).toLowerCase() === lowerValue);
};
export const useLongPress = (onLongPress, { delay = 500 } = {}) => {
    const timerRef = useRef(null);
    useEffect(() => {
        const start = () => {
            timerRef.current = setTimeout(onLongPress, delay);
        };
        const clear = () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
        const handleMouseDown = (e) => {
            if (e.button === 0)
                start();
        };
        const handleTouchStart = () => {
            start();
        };
        const handleMouseUp = clear;
        const handleMouseLeave = clear;
        const handleTouchEnd = clear;
        const handleTouchCancel = clear;
        window.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('mouseleave', handleMouseLeave);
        window.addEventListener('touchstart', handleTouchStart);
        window.addEventListener('touchend', handleTouchEnd);
        window.addEventListener('touchcancel', handleTouchCancel);
        return () => {
            window.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('mouseleave', handleMouseLeave);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchend', handleTouchEnd);
            window.removeEventListener('touchcancel', handleTouchCancel);
        };
    }, [onLongPress, delay]);
};
